import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	BettingLimitAction,
	CommonState,
	LIMIT_PERIODS,
	LimitPeriod,
	NetlossLimitSelector,
	ResponsibleGamingUiConfigService,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	CurrencySelector,
	MessageType,
} from "@obg/ngx.core";
import { CoreContentState, TranslationGroup, TranslationService } from "@obg/ngx.core.content";
import { CurrencyService, DateFormat, DateService, ignoreNil } from "@obg/ngx.global";
import { Observable } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

import { NETLOSS_LIMIT_PERIODS, TRANSLATION_PREFIX } from "./netloss-limit.const";
import {
	NetlossActiveLimit,
	NetLossLimit,
	NetlossLimitConfig,
	NetlossLimitContent,
	NetlossLimitPeriod,
	Prefix,
} from "./netloss-limit.model";

@Injectable({ providedIn: "root" })
export class NetlossLimitService {
	jurisdictionTranslatePrefix: string | undefined;

	constructor(
		private dateService: DateService,
		private alertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private store: Store<CommonState & CoreContentState>,
		private responsibleGamingUiConfigService: ResponsibleGamingUiConfigService,
		private netlossLimitSelector: NetlossLimitSelector,
		private currencyService: CurrencyService,
		private currencySelector: CurrencySelector,
		private translationService: TranslationService
	) {
		this.store.dispatch(BettingLimitAction.getNetlossLimit());
	}

	getNetlossLimitContent$(): Observable<NetlossLimitContent> {
		const keys = [
			`${Prefix.NetlossLimit}.title`,
			`${Prefix.NetlossLimit}.current-limit`,
			`${Prefix.NetlossLimit}.limit-none`,
			`${Prefix.NetlossLimit}.edit-your-limit`,
			`${Prefix.NetlossLimit}.limit`,
			`${Prefix.NetlossLimit}.caption`,
			`${Prefix.NetlossLimit}.edit-netloss-limit`,
			`${Prefix.NetlossLimit}.delete-netloss-limit`,
			`${Prefix.General}.cancel`,
			`${Prefix.NetlossLimit}.save-netloss-limit`,
		];
		return this.getCurrencyCode$().pipe(
			ignoreNil(),
			switchMap((currency) =>
				this.translationService
					.translateByJurisdictionPrefixList$({
						defaultCrsKeys: keys,
						interpolateParams: {
							currency,
						},
						dotMapping: true,
					})
					.pipe(
						map(
							([
								title,
								currentLimit,
								limitNone,
								editTitle,
								inputLabel,
								caption,
								buttonEdit,
								buttonDelete,
								buttonCancel,
								buttonSave,
							]) => ({
								title,
								currentLimit,
								limitNone,
								editTitle,
								inputLabel,
								caption,
								buttonEdit,
								buttonDelete,
								buttonCancel,
								buttonSave,
							})
						)
					)
			)
		);
	}

	getNetlossLimitComponentConfig(): NetlossLimitConfig {
		return {
			isDeleteLimitEnabled: this.responsibleGamingUiConfigService.getShowNetlossLimitDeleteBtn(),
		};
	}

	getActiveNetLossLimit$(): Observable<NetlossActiveLimit | undefined> {
		return this.store.select(this.netlossLimitSelector.getActiveNetlossLimit()).pipe(
			switchMap((activeLimit) =>
				this.translationService
					.translateByJurisdictionPrefixList$({
						defaultCrsKeys: [
							`${Prefix.NetlossLimit}.upcoming-deletion`,
							`${Prefix.NetlossLimit}.cash-amount.${activeLimit?.limitPeriod?.toLowerCase()}`,
						],
						interpolateParams: {
							endDate: this.dateService.format(activeLimit?.endDate, DateFormat.ShortDate),
							amount: this.currencyService.format(activeLimit?.limitValue),
						},
						dotMapping: true,
					})
					.pipe(
						map(([limitInfo, cashAmount]) => ({
							data: activeLimit,
							limitInfo,
							cashAmount,
						}))
					)
			)
		);
	}

	getQueuedNetLossLimit$(): Observable<NetLossLimit | undefined> {
		return this.store.select(this.netlossLimitSelector.getQueuedNetlossLimit()).pipe(
			switchMap((queuedLimit) =>
				this.translationService
					.translateByJurisdictionPrefixList$({
						defaultCrsKeys: [
							`${Prefix.NetlossLimit}.queued-limit.${queuedLimit?.limitPeriod?.toLowerCase()}`,
						],
						interpolateParams: {
							amount: this.currencyService.format(queuedLimit?.limitValue),
							startDate: this.dateService.format(queuedLimit?.startDate, "dd/MM/yyyy HH:mm"),
						},
						dotMapping: true,
					})
					.pipe(
						map(([limitInfo]) => ({
							data: queuedLimit,
							limitInfo,
						}))
					)
			)
		);
	}

	getNetLossLimitPeriods$(): Observable<NetlossLimitPeriod[] | undefined> {
		return this.translationService
			.translateByJurisdictionPrefixList$({
				defaultCrsKeys: NETLOSS_LIMIT_PERIODS.map((res) => res.label),
				dotMapping: true,
			})
			.pipe(
				map((translations) =>
					NETLOSS_LIMIT_PERIODS.map((period, index) => ({
						value: period.value,
						label: translations[index] || "",
					}))
				)
			);
	}

	getCurrencyCode$(): Observable<string | undefined> {
		return this.store.select(this.currencySelector.getByCurrentCustomer()).pipe(map((currency) => currency?.code));
	}

	/**
	 * Removes current Netloss Limit
	 * @param disableEditLayout - callback that updates property passed to MFE
	 */
	removeNetlossLimit(disableEditLayout: () => void): void {
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "NETLOSS_LIMIT_REMOVE_ALERT_ID",
				testId: "netloss-limit-deletion-confirmation",
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: `${TRANSLATION_PREFIX}.deletion-confirmation-caption`,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(BettingLimitAction.deleteNetlossLimit());
							disableEditLayout();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	/**
	 * Submit Netloss Limit request
	 * @param limit - Net-Loss limit amount
	 * @param limitPeriod - Period amount (day, week, month)
	 * @param disableEditLayout - callback that updates property passed to MFE
	 */
	submitNetlossLimit(limit: number, limitPeriod: LimitPeriod, disableEditLayout: () => void): void {
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: "NETLOSS_LIMIT_SUBMIT_ALERT_ID",
				testId: "netloss-limit-submit-confirmation",
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: `${TRANSLATION_PREFIX}.confirmation-caption`,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(
								BettingLimitAction.updateNetlossLimit({
									period: LIMIT_PERIODS.get(limitPeriod) || LimitPeriod.Daily,
									limitValue: limit,
								})
							);

							this.snackBarService
								.openSnackBar$({
									prefixJurisdiction: true,
									message: `${TRANSLATION_PREFIX}.update-success`,
									action: "general.close",
									duration: 4000,
									type: MessageType.Success,
									id: "netloss-limit-update-success",
								})
								.pipe(take(1), tap(disableEditLayout))
								// eslint-disable-next-line rxjs/no-ignored-subscription
								.subscribe();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}
}
